import { useEffect, useMemo, useRef, useState } from 'react';
import '../layout/custom/smart-spinner.css'
import { PointerPin, WheelContainer, WheelStyles } from '../../components/SpinWheel/wheelStyles';
import { Wheel } from '../utils/react-custom-roulette/components/Wheel';

const KEY_BINDINGS: Record<number, string> = {
  0: "a", // first answer
  1: "b", // second answer
  2: "c", // third answer
};

type Step = "start" | "wheel" | "quiz";

const slices = [{}, {}, {}, {}];

type Answer = { text: string; isCorrect: boolean };
interface Question {
  id: number;
  question: string;
  answers: Answer[];
}

const questions: Question[] = [
  {
    id: 1,
    question: "Which part of the marketing funnel does Smart+ specialize in?",
    answers: [
      { text: "Upper", isCorrect: false },
      { text: "Middle", isCorrect: false },
      { text: "Lower", isCorrect: true  },
    ],
  },
  {
    id: 2,
    question: "What does Smart+ take off your hands?",
    answers: [
      { text: "Coffee Runs", isCorrect: false },
      { text: "Media planning", isCorrect: false },
      { text: "Targeting, bidding, creative rotation, budgeting", isCorrect: true  },
    ],
  },
  {
    id: 3,
    question: "Which metric often gets a glow-up thanks to Smart+?",
    answers: [
      { text: "Bounce rate", isCorrect: false },
      { text: "Cost per acquisition (CPA)", isCorrect: true  },
      { text: "Watch time", isCorrect: false },
    ],
  },
  {
    id: 4,
    question: "What does Smart+ care most about optimizing for?",
    answers: [
      { text: "Clicks", isCorrect: false },
      { text: "Views", isCorrect: false },
      { text: "Conversions", isCorrect: true  },
    ],
  },
  {
    id: 5,
    question: "How does Smart+ change the media buying game?",
    answers: [
      { text: "Makes it more complicated", isCorrect: false },
      { text: "Simplifies everything and boosts efficiency", isCorrect: true  },
    ],
  },
  {
    id: 6,
    question: "Who should be using Smart+: Big brands, small brands, or both?",
    answers: [
      { text: "Just the big guys", isCorrect: false },
      { text: "Only startups", isCorrect: false },
      { text: "Both — especially performance-focused ones", isCorrect: true  },
    ],
  },
  {
    id: 7,
    question: "What's the one thing you must do before launching a Smart+ campaign?",
    answers: [
      { text: "Add a trending dance", isCorrect: false },
      { text: "Install the TikTok pixel properly", isCorrect: true  },
      { text: "Buy a ring light", isCorrect: false },
    ],
  }
];

function rand<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

function sampleWithoutById<T>(
  pool: T[],
  excludeIds: Set<number>,
  getId: (t: T) => number
): T[] {
  return pool.filter(x => !excludeIds.has(getId(x)));
}

export default function SmartSpinner() {
  const [step, setStep] = useState<Step>("start");

  const [startActive, setStartActive] = useState(false);

  const [mustSpin, setMustSpin] = useState(false);
  const [readyToSpin, setReadyToSpin] = useState(false);
  const [resetWheel, setResetWheel] = useState(false);
  const [prizeNumber, setPrizeNumber] = useState(0);

  const [tourQuestions, setTourQuestions] = useState<Question[]>([]);
  const [qIndex, setQIndex] = useState(0);
  const [locked, setLocked] = useState(false); // prevent spam clicks during 1s feedback
  const [wrongSet, setWrongSet] = useState<Set<number>>(new Set()); // for first question multi-attempt marking
  const [lastTourIds, setLastTourIds] = useState<number[] | null>(null); // to alternate first question
  const [answerJustClicked, setAnswerJustClicked] = useState<number | null>(null);

  const audioRef = useRef(new Audio(`${process.env.PUBLIC_URL}/assets/wheel-turn-effect.mp3`));

  const threeAnswerQs = useMemo(
    () => questions.filter(q => q.answers.length === 3),
    []
  );

  const startClicked = () => {
    setStartActive(true);
    setTimeout(() => {
      setStartActive(false);
      setStep("wheel");
    }, 2000);
  };

  const handleSpinClick = () => {
    if (!mustSpin) {
      setPrizeNumber(Math.floor(Math.random() * 4));
      setMustSpin(true);
    }
  };

  const onWheelStop = async () => {
    setMustSpin(false);
    setReadyToSpin(true);

    const newTour = buildTour(lastTourIds);
    setTourQuestions(newTour);
    setQIndex(0);
    setWrongSet(new Set());

    setStep("quiz")
    setResetWheel(true);
  };

  useEffect(() => {
    if (mustSpin) {
      audioRef.current.play();
    } else {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  }, [mustSpin]);

  useEffect(() => {
    if (resetWheel) {
      setMustSpin(false);
      setResetWheel(false);
      setReadyToSpin(false);
    }
  }, [resetWheel]);

  function buildTour(prevTourIds: number[] | null): Question[] {
    // Pick first question: must have 3 answers and NOT be in prevTourIds (if present)
    const exclude = new Set(prevTourIds ?? []);
    const firstCandidates = sampleWithoutById(threeAnswerQs, exclude, q => q.id);
    const firstQ = firstCandidates.length ? rand(firstCandidates) : rand(threeAnswerQs);

    // Q2 & Q3 can be ANY questions (including 2-answer), just not duplicates within this tour
    const remainingIds = new Set<number>([firstQ.id]);
    const pool = questions.filter(q => !remainingIds.has(q.id));
    const secondQ = rand(pool);
    remainingIds.add(secondQ.id);
    const thirdPool = questions.filter(q => !remainingIds.has(q.id));
    const thirdQ = rand(thirdPool);

    // small shuffle between Q2 and Q3
    const tail = Math.random() < 0.5 ? [secondQ, thirdQ] : [thirdQ, secondQ];
    return [firstQ, ...tail];
  }

  const currentQ = tourQuestions[qIndex];

  function proceedAfterDelay(next: () => void) {
    setLocked(true);
    setTimeout(() => {
      setLocked(false);
      next();
    }, 1000); // 1s feedback
  }

  function resetToStartAfterDelay() {
    setLocked(true);
    setTimeout(() => {
      setLocked(false);
      // record last tour ids for alternating rule
      setLastTourIds(tourQuestions.map(q => q.id));
      // go back to start
      setStep("start");
    }, 1000);
  }

  function goNextQuestionOrFinish() {
    if (qIndex >= 2) {
      // finished 3rd question → record last tour and return to start
      setLastTourIds(tourQuestions.map(q => q.id));
      setStep("start");
    } else {
      setQIndex(i => i + 1);
      setWrongSet(new Set()); // clear wrong marks for the new question
    }
  }

  function onAnswerClick(answerIdx: number) {
    if (!currentQ || locked) return;
    setAnswerJustClicked(answerIdx);

    const isCorrect = currentQ.answers[answerIdx]?.isCorrect ?? false;

    // First question in the tour: allow multiple attempts (A→B→C) and keep wrong choices marked until the correct one is picked.
    if (qIndex === 0) {
      if (isCorrect) {
        proceedAfterDelay(goNextQuestionOrFinish);
      } else {
        // mark this choice wrong and stay on the same question (do not lock for long)
        setWrongSet(prev => new Set(prev).add(answerIdx));
        // tiny guard to avoid ultra-fast double clicking; UI shows wrong immediately
        setLocked(true);
        setTimeout(() => setLocked(false), 250);
      }
      return;
    }

    // For Q2 and Q3: single attempt only.
    if (isCorrect) {
      proceedAfterDelay(goNextQuestionOrFinish);
    } else {
      // If user answers wrong on Q2 or Q3 → reset to start
      setWrongSet(new Set([answerIdx]));
      proceedAfterDelay(goNextQuestionOrFinish);
    }
  }

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (step !== "quiz" || !currentQ || locked) return;

      const pressedKey = e.key.toLowerCase();
      // check each answer slot against KEY_BINDINGS
      currentQ.answers.forEach((_, i) => {
        if (KEY_BINDINGS[i] === pressedKey) {
          onAnswerClick(i);
        }
      });
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [step, currentQ, locked]);

  return (
    <div id="smart-spinner">
      {step === "start" && (
        <div className="step-box start" onClick={startClicked}>
          {/* Start Screen */}
          <img src={`${process.env.PUBLIC_URL}/assets/smart-spinner/tiktok-world-logo.png`} className="spinner-logo-big" />
          <h1 className={startActive ? "active" : ""}>
            {startActive ? "Please Wait" : "Smart+ Spinner"}
          </h1>
        </div>
      )}
      {step === "wheel" && (
        <div className="step-box inner wheel">
          {/* Start Screen */}
          <img src={`${process.env.PUBLIC_URL}/assets/smart-spinner/tiktok-world-logo.png`} className="spinner-logo-small" />
          <WheelStyles
            wheelDiameter={700}
          >
            <WheelContainer
              className="wheel-container"
              decorated={false}
              imageUrl={`${process.env.PUBLIC_URL}/assets/wheel-decoration.png`}
              spinnerRotate={30}
              spinnerCoverOpacity={false}
              onClick={handleSpinClick}
            >
              <PointerPin
                className={"pointer-pin " + (mustSpin ? "spinning" : "")}
                spinDuration={5}
                speedMode={false}
                pinColor="`#fff`"
                bgImage={`${process.env.PUBLIC_URL}/assets/smart-spinner/tiktok-spinner-pin.png`}
              />
              <img src={`${process.env.PUBLIC_URL}/assets/smart-spinner/tiktok-spinner-center.png`} className="center-img" alt="" />
              <Wheel         
                mustStartSpinning={mustSpin}
                readyToSpin={readyToSpin}
                onStopSpinning={onWheelStop}
                prizeNumber={prizeNumber}
                spinDuration={5 / 10}
                data={slices.map(() => ({ option: "", probability: undefined }))}
                startingOptionIndex={0}
                backgroundColors={["#3e68ec", "#ec145a", "#96f3b0", "#3abebf"]}
                innerRadius={20}
                radiusLineWidth={0}
                spinnerRotate={30}
              />
            </WheelContainer>
          </WheelStyles>
        </div>
      )}
      {step === "quiz" && currentQ && (
        <div
          className="step-box inner quiz"
          style={{
            background: `url(${process.env.PUBLIC_URL}/assets/smart-spinner/tiktok-bg.png) center center no-repeat, #000`,
            backgroundSize: "cover"
          }}
        >
          <img src={`${process.env.PUBLIC_URL}/assets/smart-spinner/tiktok-world-logo.png`} className="spinner-logo-small" />
          <h3>Smart+ Spinner</h3>
          <div className="quiz-box">
            <p className="question">{currentQ.question}</p>
            <ul className="answers">
              {currentQ.answers.map((a, i) => {
                // class rules:
                // - During first question, wrong picks stay painted 'wrong' until correct is chosen.
                // - During Q2/Q3, if the single attempt is wrong we highlight it for the 1s before reset.
                const isMarkedWrong = wrongSet.has(i);
                const isMarkedCorrect = locked && a.isCorrect && !isMarkedWrong && i === answerJustClicked;
                return (
                  <li
                    key={i}
                    className={
                      isMarkedCorrect ? "correct" : isMarkedWrong ? "wrong" : ""
                    }
                    onClick={() => onAnswerClick(i)}
                  >
                    {a.text}
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}
    </div>
  )
}